/**
 * useMarket — tenant home-market context (region-aware portal).
 *
 * Exposes the authenticated tenant's `{ home_country, currency, vat_rate,
 * number_locale }` from `GET /api/v1/tenants/me` via TanStack Query (server
 * state), plus a bound `money(cents, currency?)` formatter so call sites don't
 * thread `currency`/`locale` by hand.
 *
 * While the request is in flight (or errored) it returns NL/EUR defaults, which
 * reproduces today's behaviour for the existing NL tenants — never a flash of
 * the wrong currency for them, and a safe fallback for everyone else.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { formatMoney } from '@/lib/marketConfig';

export interface Market {
  home_country: string;
  currency: string;
  vat_rate: number;
  number_locale: string;
}

// NL/EUR — matches the historical hard-coded default; used until /tenants/me
// resolves, so existing tenants never see a wrong-currency flash.
export const DEFAULT_MARKET: Market = {
  home_country: 'NL',
  currency: 'EUR',
  vat_rate: 0.21,
  number_locale: 'nl-NL',
};

export interface UseMarketResult extends Market {
  /** Format cents in the tenant locale. Pass a per-cell `currency` to override
   *  the tenant currency for that value (overview cells carry their own). */
  money: (cents: number, currency?: string) => string;
}

export function useMarket(): UseMarketResult {
  const { data } = useQuery({
    queryKey: ['tenant-me'],
    queryFn: () => api.tenants.me(),
    staleTime: 5 * 60_000,
  });

  const market: Market = data
    ? {
        home_country: data.home_country,
        currency: data.currency,
        vat_rate: data.vat_rate,
        number_locale: data.number_locale,
      }
    : DEFAULT_MARKET;

  return {
    ...market,
    money: (cents: number, currency?: string) =>
      formatMoney(cents, currency ?? market.currency, market.number_locale),
  };
}
